import React, { useContext, useEffect, useMemo, useState } from "react";
import Widget from "../../../../../components/Widget";
import style from "./ExamEditWidget.module.scss";
import { Affix, Col, Icon, Row, Tooltip } from "antd";
import WidgetQuestBlock from "../WidgetQuestBlock";
import { QuestionType } from "../../../../../service/service";
import { countTotalScore } from "../../../../../service/homework";
import PaperContext from "../../context";
import GoBackButton from "pages/homework/component/goBackButton";
import {
    SortableContainer,
    SortableElement,
    SortableElementProps,
    SortableHandle
} from "react-sortable-hoc";
import arrayMove from "array-move";
import { OrderTopicType } from "../../index";

export default function ExamEditWidget({
    examTypeList,
    sortType
}: {
    examTypeList: QuestionType[];
    sortType: (topicTypes: QuestionType[], sort: boolean) => void;
}) {
    const { topics, examData, lockEdit } = useContext(PaperContext);
    const [activePanel, setActivePanel] = useState("");
    const totalScore = useMemo(() => countTotalScore(topics), [topics]);

    const [topicTypes, setTopicTypes] = useState(examTypeList);

    useEffect(() => {
        setTopicTypes(examTypeList);
    }, [examTypeList]);

    const onToggle = (active: boolean, type: QuestionType) => {
        setActivePanel(active ? type : "");
    };

    const handleSortType = ({
        oldIndex,
        newIndex
    }: {
        oldIndex: number;
        newIndex: number;
    }) => {
        const array = arrayMove(topicTypes, oldIndex, newIndex);
        setTopicTypes(array);
        sortType(array, true);
    };
    const filterTopicByType = (type: QuestionType) => {
        return topics.filter((item: any) => item.topicType === type);
    };

    const DragHandle = SortableHandle(() => (
        <Tooltip title="鼠标左键点击图标拖拽调整顺序">
            <Icon type="drag" />
        </Tooltip>
    ));

    const SortableItem = SortableElement(
        ({ type, className }: { type: QuestionType; className?: string }) => {
            return (
                <div className={className}>
                    <WidgetQuestBlock
                        end={<DragHandle />}
                        list={filterTopicByType(type)}
                        type={type}
                        active={activePanel === type}
                        onToggle={active => onToggle(active, type)}
                    />
                </div>
            );
        }
    );

    return (
        <ExamEditWidgetPure
            examName={examData?.name || ""}
            totalScore={totalScore}
            topics={topics}
            handleSortType={handleSortType}
            examTypeList={examTypeList}
            onToggle={onToggle}
            topicTypes={topicTypes}
            SortableItem={SortableItem}
        />
    );
}

// 纯组件
interface ExamEditWidgetPureProps {
    examName: string;
    totalScore: ReturnType<typeof countTotalScore>;
    topics: OrderTopicType[];
    handleSortType: (args: { oldIndex: number; newIndex: number }) => void;
    examTypeList: QuestionType[];
    onToggle: (active: boolean, type: QuestionType) => void;
    topicTypes: QuestionType[];
    SortableItem: React.ComponentClass<
        { type: QuestionType; className?: string } & SortableElementProps
    >;
    children?: React.ReactNode;
}

export function ExamEditWidgetPure({
    examName,
    totalScore,
    topics,
    handleSortType,
    children,
    topicTypes,
    SortableItem
}: ExamEditWidgetPureProps) {
    const SortableList = useMemo(() => {
        return SortableContainer(({ list }: { list: QuestionType[] }) => {
            return (
                <div>
                    {list.map((type, index) => (
                        <SortableItem
                            className={style.sortableitem}
                            key={type}
                            index={index}
                            type={type}
                        />
                    ))}
                </div>
            );
        });
    }, []);

    return (
        <Affix>
            <Widget title="建卷" bodyPadding extraTools={<GoBackButton />}>
                <div className={style.root}>
                    <Row className={style.item}>
                        <Col span={6} className={style.label}>
                            考核名：
                        </Col>
                        <Col className={style.title} span={18}>
                            {examName}
                        </Col>
                    </Row>

                    <Row className={style.item}>
                        <Col span={6} className={style.label}>
                            总分：
                        </Col>
                        <Col span={6}>{totalScore}分</Col>
                        <Col span={4} className={style.label}>
                            题数：
                        </Col>
                        <Col span={8}>{topics.length}</Col>
                    </Row>

                    <Row>
                        <SortableList
                            useDragHandle
                            onSortEnd={handleSortType}
                            lockAxis="y"
                            list={topicTypes}
                        />
                    </Row>
                </div>

                <div>{children}</div>
            </Widget>
        </Affix>
    );
}
